import { FC, Suspense } from 'react';
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  Alert,
  Badge,
  Tabs,
  TextLink,
  ActivityIndicator,
} from '@kontentino/ui';
import { isAnalyticsReportEditorRoute } from 'app/modules/analytics/modules/reports/utils/analyticsReportsRoutes';
import AnalyticsSidebar from 'app/modules/analytics/components/AnalyticsSidebar';
import AnalyticsContentHeader from 'app/modules/analytics/components/AnalyticsContentHeader';
import AnalyticsSyncBackgroundStrip from 'app/modules/analytics/components/AnalyticsSyncBackgroundStrip';
import { AnalyticsAddNewProfileModal } from 'app/modules/analytics/components/modals/AnalyticsAddNewProfileModal';
import AnalyticsAssociateProfileModal from 'app/modules/analytics/components/modals/AnalyticsAssociateProfileModal';
import AnalyticsNoProfilesEmptyState from 'app/modules/analytics/components/AnalyticsNoProfilesEmptyState';
import AnalyticsPageSkeleton, {
  AnalyticsPageSkeletonVariant,
} from 'app/modules/analytics/components/AnalyticsPageSkeleton';
import {
  AnalyticsProvider,
  useAnalyticsContext,
} from 'app/modules/analytics/context/AnalyticsContext';
import AnalyticsHealthDriver from 'app/modules/analytics/components/AnalyticsHealthDriver';
import AnalyticsSyncDriver, {
  useAnalyticsSyncState,
} from 'app/modules/analytics/components/AnalyticsSyncDriver';
import routeConfig from 'app/config/routes';
import { useTranslation } from 'react-i18next';
import UserPermissionGate from 'components/UserPermissionGate';
import ModalRoute from 'context/ModalRouter';
import routes, { modalRoutes } from 'constants/routes';
import { UserPermission } from 'constants/userPermission';
import clsx from 'clsx';
import ReportRunPollingDriver from 'app/modules/analytics/modules/reports/components/ReportRunPollingDriver';
import { ActiveReportRunsProvider } from 'app/modules/analytics/modules/reports/context/ActiveReportRunsContext';

const { overview, performance, content, competitionAnalysis, reports } =
  routeConfig.analytics.routes;

const AnalyticsLayoutInner: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    isSyncing,
    isShowingSuccess,
    status: syncStatus,
    syncError,
    syncPageProgress,
    syncTargetLabel,
    syncTargetIsGroup,
    syncStartedAt,
    syncTargetMatchesSelection,
    jumpToSyncTarget,
    cancelSync,
    dismissSyncError,
  } = useAnalyticsSyncState();
  const { hasSessionAuthError, pages, profileGroups, isLoading } =
    useAnalyticsContext();
  const hasNoProfiles =
    !isLoading && pages.length === 0 && profileGroups.length === 0;

  const showBackgroundSyncStrip =
    !hasNoProfiles &&
    !hasSessionAuthError &&
    (isSyncing || isShowingSuccess || syncStatus === 'error');

  const showSyncSelectionMismatchBanner =
    !hasNoProfiles &&
    !hasSessionAuthError &&
    isSyncing &&
    !syncTargetMatchesSelection;

  const stripProgress = syncPageProgress ?? { completed: 0, total: 0 };

  const isReportEditorRoute = isAnalyticsReportEditorRoute(pathname);

  const isReportsSection =
    pathname.endsWith(`/${reports.path}`) ||
    pathname.includes(`/${reports.path}/`);

  const activeTab = isReportsSection
    ? reports.path
    : pathname.endsWith(`/${content.path}`)
    ? content.path
    : pathname.endsWith(`/${competitionAnalysis.path}`)
    ? competitionAnalysis.path
    : pathname.endsWith(`/${performance.path}`)
    ? performance.path
    : overview.path;

  const routeSkeletonVariant: AnalyticsPageSkeletonVariant | null =
    activeTab === content.path
      ? 'content'
      : activeTab === performance.path
      ? 'performance'
      : null;

  return (
    <div className="tw-relative tw-flex tw-h-full tw-w-full tw-overflow-hidden">
      {!isReportEditorRoute && (
        <aside className="tw-h-full tw-w-[216px] tw-shrink-0">
          <AnalyticsSidebar />
        </aside>
      )}
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
        {!hasNoProfiles && !isReportEditorRoute && (
          <div className="tw-mx-auto tw-w-full tw-max-w-[1346px] tw-px-2 tw-pt-6 md:tw-px-4">
            <h1 className="tw-mb-6 tw-flex tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-text-grayscale-180">
              {t('analytics.title')} <Badge variant="primary">Beta</Badge>
            </h1>
            <Tabs.Root value={activeTab} onValueChange={navigate}>
              <Tabs.List className="tw-border-grayscale-20">
                <Tabs.Tab
                  value={overview.path}
                  data-cy="analytics-overview-tab"
                >
                  {t('overview', { defaultValue: 'Overview' })}
                </Tabs.Tab>
                <Tabs.Tab
                  value={performance.path}
                  data-cy="analytics-performance-tab"
                >
                  {t('performance', { defaultValue: 'Performance' })}
                </Tabs.Tab>
                <Tabs.Tab value={content.path} data-cy="analytics-content-tab">
                  {t('content', { defaultValue: 'Content' })}
                </Tabs.Tab>
                <Tabs.Tab
                  value={competitionAnalysis.path}
                  data-cy="analytics-competition-analysis-tab"
                >
                  {t('navigation.analytics.competition', {
                    defaultValue: 'Competition analysis',
                  })}
                </Tabs.Tab>
                <Tabs.Tab value={reports.path} data-cy="analytics-reports-tab">
                  {t('analyticsReports.tab', { defaultValue: 'Reports' })}
                </Tabs.Tab>
              </Tabs.List>
            </Tabs.Root>
          </div>
        )}
        {!hasNoProfiles && !isReportEditorRoute && (
          <div className="tw-relative tw-z-[1] tw-mx-auto tw-w-full tw-max-w-[1346px] tw-px-2 md:tw-px-4">
            {activeTab !== reports.path &&
              activeTab !== competitionAnalysis.path && (
                <AnalyticsContentHeader />
              )}
            {showBackgroundSyncStrip &&
              (syncTargetLabel.length > 0 || syncStatus === 'error') && (
                <AnalyticsSyncBackgroundStrip
                  targetLabel={syncTargetLabel}
                  completed={stripProgress.completed}
                  total={stripProgress.total}
                  isCompleted={isShowingSuccess}
                  isError={syncStatus === 'error'}
                  errorText={syncError ?? undefined}
                  targetIsGroup={syncTargetIsGroup}
                  syncStartedAt={syncStartedAt}
                  showJump={
                    !syncTargetMatchesSelection && syncTargetLabel.length > 0
                  }
                  onJump={jumpToSyncTarget}
                  onCancel={cancelSync}
                  onClose={dismissSyncError}
                />
              )}
          </div>
        )}
        <main className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
          <div
            className={clsx('tw-flex-1', {
              'tw-overflow-y-auto': !isReportEditorRoute,
              'tw-flex tw-min-h-0 tw-flex-col': isReportEditorRoute,
            })}
          >
            <div
              className={clsx(
                !isReportEditorRoute &&
                  'tw-mx-auto tw-my-5 tw-max-w-[1346px] tw-px-2 md:tw-px-4',
                isReportEditorRoute &&
                  'tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-px-0',
              )}
            >
              {hasNoProfiles && isReportEditorRoute ? (
                <Navigate replace to={routes.ANALYTICS_REPORTS} />
              ) : hasNoProfiles ? (
                <AnalyticsNoProfilesEmptyState />
              ) : hasSessionAuthError ? (
                <div className="tw-flex tw-min-h-[40vh] tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-p-6">
                  <Alert
                    variant="danger"
                    title={t('analytics.sessionExpired.title')}
                    data-cy="analytics_session-expired_alert"
                    cta={{
                      label: t('analytics.sessionExpired.reload'),
                      onClick: () => {
                        window.location.reload();
                      },
                      position: 'bottom',
                    }}
                    className="tw-max-w-lg"
                  >
                    <p className="tw-mb-3">
                      {t('analytics.sessionExpired.description')}
                    </p>
                    <TextLink>
                      <Link
                        to={routes.LOGIN}
                        data-cy="analytics_session-expired_go-to-login"
                      >
                        {t('analytics.sessionExpired.goToLogin')}
                      </Link>
                    </TextLink>
                  </Alert>
                </div>
              ) : (
                <>
                  {!isReportEditorRoute && showSyncSelectionMismatchBanner && (
                    <Alert
                      variant="info"
                      className="tw-mb-4"
                      data-cy="analytics_sync-mismatch_banner"
                    >
                      {t('analyticsSyncMismatchBanner', {
                        target: syncTargetLabel,
                      })}
                    </Alert>
                  )}
                  <div
                    className={clsx(
                      isReportEditorRoute &&
                        'tw-flex tw-min-h-0 tw-flex-1 tw-flex-col',
                    )}
                  >
                    <Suspense
                      fallback={
                        routeSkeletonVariant ? (
                          <AnalyticsPageSkeleton
                            variant={routeSkeletonVariant}
                          />
                        ) : (
                          <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-p-6">
                            <ActivityIndicator className="tw-text-grayscale-100" />
                          </div>
                        )
                      }
                    >
                      <Outlet />
                    </Suspense>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const AnalyticsLayout: FC = () => {
  return (
    <AnalyticsProvider>
      <UserPermissionGate scopes={UserPermission.PAGE_CREATE}>
        <ModalRoute
          path={modalRoutes.NEW_PROFILE}
          component={AnalyticsAddNewProfileModal}
        />
        <ModalRoute
          path={modalRoutes.ASSOCIATE_POSTS_PROFILE}
          component={AnalyticsAssociateProfileModal}
        />
      </UserPermissionGate>
      <ActiveReportRunsProvider>
        <ReportRunPollingDriver />
        <AnalyticsHealthDriver />
        <AnalyticsSyncDriver />
        <AnalyticsLayoutInner />
      </ActiveReportRunsProvider>
    </AnalyticsProvider>
  );
};

export default AnalyticsLayout;
